import { motion } from "framer-motion";
import { Flower2, Sparkles, Sun } from "lucide-react";
import { IMAGES } from "../data/content";

const VALUES = [
    { icon: Sun, title: "Warmth", text: "Comfort, love and familiarity in every plate.", testid: "esak-value-warmth" },
    { icon: Flower2, title: "Freshness", text: "Light, modern, blooming with life and flavour.", testid: "esak-value-freshness" },
    { icon: Sparkles, title: "Surprise", text: "Unexpected twists that keep every visit memorable.", testid: "esak-value-surprise" },
];

export const EsakSection = () => {
    return (
        <section id="esak" className="relative py-24 md:py-36 px-6 md:px-10 overflow-hidden" style={{ backgroundColor: "#F8F6F0" }} data-testid="esak-section">
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-40 blur-3xl" style={{ backgroundColor: "#F9D5D3" }} />
            <div className="absolute -bottom-24 -left-24 w-[28rem] h-[28rem] rounded-full opacity-40 blur-3xl" style={{ backgroundColor: "#E4D3E9" }} />

            <div className="relative max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        <p className="font-serif-brand italic text-xl mb-4" style={{ color: "#E50046" }}>
                            Love and flavour, with a fresh twist
                        </p>
                        <img
                            src="/brand/esak-logo-pink.webp"
                            alt="Esak, flavours for the modern heart"
                            className="w-64 md:w-[26rem] h-auto"
                            data-testid="esak-logo"
                        />
                        <p className="sr-only">esak. flavours for the modern heart</p>
                        <p className="mt-8 text-base md:text-lg leading-relaxed text-[#1C1C1E]/70 max-w-xl" data-testid="esak-body">
                            Esak is not a brand, it's a feeling served on every plate. Set in a
                            world of tulips, textures and tasteful colour, our kitchen serves
                            familiar flavours with a fresh twist: light, colourful, and full of
                            love. Timeless at heart, trending in spirit, modern love on a plate.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="esak-palette">
                            {["#E50046", "#E4D3E9", "#F9D5D3", "#C9E8DD", "#D3E7F5", "#FFF1DC", "#D6FAFA"].map((c) => (
                                <span key={c} className="w-8 h-8 rounded-full border border-black/10 shadow-sm" style={{ backgroundColor: c }} data-testid={`esak-swatch-${c.slice(1)}`} />
                            ))}
                        </div>

                        <a
                            href="#menu"
                            data-testid="esak-menu-button"
                            className="mt-10 inline-block rounded-full text-white font-semibold px-9 py-4 shadow-xl hover:brightness-110 hover:-translate-y-0.5 transition-[transform,filter] duration-200"
                            style={{ backgroundColor: "#E50046", boxShadow: "0 20px 40px -12px rgba(229,0,70,0.45)" }}
                        >
                            See the Esak Menu
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <img
                            src={IMAGES.esakDishMain}
                            alt="A signature Esak dish garnished with flowers"
                            className="rounded-[2.5rem] w-full aspect-[3/4] object-cover shadow-2xl shadow-[#E50046]/15"
                            data-testid="esak-food-image"
                        />
                        <img
                            src={IMAGES.esakDishSmall}
                            alt="Bright garden seating at Uncover Avenue"
                            className="absolute -bottom-12 -left-4 md:-left-8 w-44 md:w-56 aspect-[3/4] object-cover rounded-3xl border-8 shadow-xl rotate-2"
                            style={{ borderColor: "#F8F6F0" }}
                            data-testid="esak-food-image-small"
                        />
                    </motion.div>
                </div>

                <div className="mt-32 grid sm:grid-cols-3 gap-6" data-testid="esak-values-grid">
                    {VALUES.map((v, i) => (
                        <motion.div
                            key={v.title}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="rounded-3xl p-8 bg-white shadow-lg shadow-[#1C1C1E]/5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                            data-testid={v.testid}
                        >
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: "#E5004612" }}>
                                <v.icon size={22} style={{ color: "#E50046" }} />
                            </div>
                            <h3 className="font-syne text-xl font-bold text-[#1C1C1E] mb-2">{v.title}</h3>
                            <p className="text-sm leading-relaxed text-[#1C1C1E]/60">{v.text}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
